<script setup lang="ts">
/**
 * Carbon inline notification — banner with a 3px coloured left rule, a
 * tinted background, a small status icon, a bold title, and a body.
 * Matches `preview/components-notifications.html` in the design bundle.
 *
 * `kind` chooses the layout:
 *   • `inline` — flush in flow, no shadow
 *   • `toast` — same chrome, plus a `box-shadow` so it floats over content
 *
 * The close button is always rendered unless `hideClose` is set. The
 * button emits `close`; the parent decides whether to mount or unmount.
 */
import { computed } from 'vue'
import CdsIcon, { type CdsIconName } from './CdsIcon.vue'

type Variant = 'info' | 'warning' | 'error' | 'success'
type Kind = 'inline' | 'toast'

const props = withDefaults(
  defineProps<{
    variant?: Variant
    kind?: Kind
    title: string
    /** Optional body text. Use the default slot for richer content. */
    subtitle?: string
    hideClose?: boolean
    /** Accessible label for the close button. */
    closeLabel?: string
  }>(),
  {
    variant: 'info',
    kind: 'inline',
    subtitle: undefined,
    hideClose: false,
    closeLabel: 'Chiudi',
  },
)

defineEmits<{ close: [] }>()

const iconName = computed<CdsIconName>(() => {
  switch (props.variant) {
    case 'success':
      return 'check-circle'
    case 'warning':
      return 'alert-triangle'
    case 'error':
      return 'alert-circle'
    case 'info':
    default:
      return 'help'
  }
})

const role = computed(() =>
  props.variant === 'error' || props.variant === 'warning' ? 'alert' : 'status',
)
</script>

<template>
  <div
    class="fz-note"
    :class="[`fz-note--${variant}`, `fz-note--${kind}`]"
    :role="role"
    aria-live="polite"
  >
    <span class="fz-note__icon" aria-hidden="true">
      <CdsIcon :name="iconName" :size="20" />
    </span>
    <div class="fz-note__body">
      <p class="fz-note__title">{{ title }}</p>
      <p v-if="subtitle" class="fz-note__desc">{{ subtitle }}</p>
      <div v-if="$slots.default" class="fz-note__slot">
        <slot />
      </div>
    </div>
    <button
      v-if="!hideClose"
      type="button"
      class="fz-note__close"
      :aria-label="closeLabel"
      @click="$emit('close')"
    >
      <CdsIcon name="close" :size="16" />
    </button>
  </div>
</template>

<style scoped>
.fz-note {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-04);
  padding: var(--spacing-04) var(--spacing-05);
  border-left: 3px solid;
  font-family: var(--font-sans);
  background: var(--bg);
}
.fz-note--toast {
  box-shadow: var(--shadow-overlay);
  background: var(--bg);
}
.fz-note__icon {
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.fz-note__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-02);
}
.fz-note__title {
  font-size: var(--fs-14);
  font-weight: var(--fw-semibold);
  color: var(--text-primary);
  margin: 0;
}
.fz-note__desc {
  font-size: var(--fs-14);
  color: var(--text-secondary);
  line-height: 1.43;
  margin: 0;
}
.fz-note__slot {
  font-size: var(--fs-14);
  color: var(--text-secondary);
}
.fz-note__close {
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--icon-secondary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
}
.fz-note__close:hover {
  color: var(--icon-primary);
}
.fz-note__close:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: -2px;
}
.fz-note--info {
  border-color: var(--support-info);
  background: var(--blue-10);
}
.fz-note--info .fz-note__icon {
  color: var(--support-info);
}
.fz-note--success {
  border-color: var(--support-success);
  background: #defbe6;
}
.fz-note--success .fz-note__icon {
  color: var(--support-success);
}
.fz-note--warning {
  border-color: var(--support-warning);
  background: #fcf4d6;
}
.fz-note--warning .fz-note__icon {
  color: var(--support-caution-major);
}
.fz-note--error {
  border-color: var(--support-error);
  background: var(--red-10);
}
.fz-note--error .fz-note__icon {
  color: var(--support-error);
}
</style>
